import { createTrackingConfig, getLabels } from '../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../config/appConfig';

const employeeTrackingLabels = getLabels('Employee');
const employeeTrackingBase = createTrackingConfig('Employee');

export const employeeConfig = {
  entityName: 'Employee',
  
  // Entity information for file attachments
  entityTypeId: null, // Employee doesn't use file attachments
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/employee',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete'
    },
    lookupEndpoints: {
      departments: '/department'
    },
    // SWR invalidation endpoints - for refreshing data after operations
    swrInvalidation: {
      mainList: '/employee'
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '30%',
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default',
    splitView: true,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth
  },

  // Features configuration
  features: {
    fileAttachments: false, // Disable file attachment functionality for employees
    masterEntityTypeId: null, // Employee doesn't have entity type for audit logs
  },
  
  // Custom handlers
  customHandlers: {
    onSave: null,
    onDelete: null,
    onLoad: null,
    onFormOpen: null
  },
  
  // Service configuration
  service: {
    name: 'employeeService',
    methods: {
      getAll: 'getAllEmployees',
      getById: 'getEmployeeById', 
      create: 'createEmployee',
      update: 'updateEmployee',
      delete: 'deleteEmployee',
      validate: 'validateData'
    }
  },
  
  // Display Configuration
  displayName: 'Employee Management',
  description: 'Employee management system for training assignments',
  icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'department', 'createdBy', 'updatedBy',
    'createdById', 'updatedById',
    'createdAt', 'updatedAt', 'trainingAssignments', 'trainingLogs',
  ],

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'employeeNumber',
      label: 'Employee Number',
      type: 'text',
      required: false,
      placeholder: 'Enter employee number',
      table: { header: 'Emp. Number', width: '140px', sortable: true },
      filterable: true,
      validation: {
        required: false,
        maxLength: 50,
        message: 'Employee number must not exceed 50 characters'
      }
    },
    {
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
      placeholder: 'Enter first name',
      table: { header: 'First Name', width: '150px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'First name is required and must not exceed 100 characters'
      }
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      placeholder: 'Enter last name',
      table: { header: 'Last Name', width: '150px', sortable: true },
      filterable: true,
      validation: {
        required: true,
        maxLength: 100,
        message: 'Last name is required and must not exceed 100 characters'
      }
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Enter email address',
      table: { header: 'Email', width: '200px', sortable: true },
      filterable: true,
      validation: {
        required: false,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address'
      }
    },
    {
      name: 'phone',
      label: 'Phone',
      type: 'text',
      required: false,
      placeholder: 'Enter phone number',
      table: { header: 'Phone', width: '140px', sortable: true },
      filterable: true,
      validation: {
        required: false,
        maxLength: 50,
        message: 'Phone number must not exceed 50 characters'
      }
    },
    {
      name: 'departmentId',
      label: 'Department',
      type: 'select',
      required: false,
      lookupEndpoint: 'departments',
      options: [],
      table: { 
        header: 'Department', 
        width: '150px', 
        sortable: true,
        displayField: 'department'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid department'
      }
    },
    {
      name: 'position',
      label: 'Position',
      type: 'text',
      required: false,
      placeholder: 'Enter job position',
      table: { header: 'Position', width: '150px', sortable: true },
      filterable: true,
      validation: {
        required: false,
        maxLength: 100,
        message: 'Position must not exceed 100 characters'
      }
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      required: false,
      defaultValue: true,
      switchLabel: 'Employee is active',
      labelPosition: 'left',
      table: { 
        header: 'Active', 
        width: '80px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        required: false
      }
    },
    {
      name: 'createdAt',
      label: 'Created',
      type: 'date',
      required: false,
      table: { 
        header: 'Created', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'updatedAt',
      label: 'Updated',
      type: 'date',
      required: false,
      table: false,
      filterable: false
    }
  ],
  
  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    
    return columns;
  },

  exportFileName: 'qms_employees',
  
  // Backend field mappings
  fieldMappings: {
    department: 'department.name',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    isActive: ['true', 'false'], // Active employees first
    lastName: 'asc' // Alphabetical by last name
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['firstName', 'lastName', 'email', 'departmentId', 'isActive']
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Employee Information',
      icon: '👤',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['employeeNumber', 'firstName', 'lastName', 'email', 'phone', 'departmentId', 'position', 'isActive'],
      fieldSpans: {
        'employeeNumber': 'md:col-span-12',
        'firstName': 'md:col-span-6',
        'lastName': 'md:col-span-6',
        'email': 'md:col-span-6',
        'phone': 'md:col-span-6',
        'departmentId': 'md:col-span-12',
        'position': 'md:col-span-12',
        'isActive': 'md:col-span-12'
      }
    }
  ],

  // Tracking — show audit users by display name (nested objects from API / selected row)
  trackingSection: {
    ...employeeTrackingBase,
    fields: employeeTrackingBase.fields.map((f) =>
      f.id === 'created'
        ? {
            ...f,
            userLabel: 'Created user:',
            dateLabel: employeeTrackingLabels.createdDateLabel,
            userEmptyText: employeeTrackingLabels.notSpecifiedText,
            dateEmptyText: employeeTrackingLabels.notSpecifiedText,
          }
        : f.id === 'updated'
          ? {
              ...f,
              userLabel: 'Updated user:',
              dateLabel: employeeTrackingLabels.updatedDateLabel,
              userEmptyText: employeeTrackingLabels.notModifiedText,
              dateEmptyText: employeeTrackingLabels.neverUpdatedText,
            }
          : f,
    ),
  },

  // Labels and Text Configuration (uses predefined Employee labels + custom overrides)
  labels: {
    // Using predefined Employee labels from centralized labels file
    // You can override any label by uncommenting and modifying:
    singular: 'Employee',
    plural: 'Employees',
    pageTitle: 'Employee Management',
    pageSubtitle: 'Manage employees for training assignments and compliance tracking',
    searchPlaceholder: 'Search employees by name, email, or employee number...',
    viewTitle: 'View Employee',
    noSelectionText: 'Select an employee to see details'
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;

    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = ['departmentId'];
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading employees...',
    noData: 'No employees found. Create your first employee to get started.',
    loadError: 'Failed to load employees',
    saveSuccess: 'Employee saved successfully!',
    saveError: 'Failed to save employee',
    deleteSuccess: 'Employee deleted successfully!',
    deleteError: 'Failed to delete employee',
    deleteConfirm: 'Are you sure you want to delete this employee? This action cannot be undone.'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null, // Will be set after validation module loads

  // Help text for field descriptions
  helpText: {
    employeeNumber: { short: 'Unique employee identification number.' },
    firstName: { short: 'Employee first name.' },
    lastName: { short: 'Employee last name.' },
    email: { short: 'Employee email address for communication and notifications.' },
    phone: { short: 'Employee phone number for contact purposes.' },
    departmentId: { short: 'Department the employee belongs to.' },
    position: { short: 'Job position or title within the organization.' },
    isActive: { short: 'Whether the employee is currently active and can be assigned trainings.' }
  }
};

export default employeeConfig;

